// Recent card — CLOSED tasks in the workstream, most recently updated
// first (updated_at, a proxy for close time; see "WHEN" below), so the
// operator can cherry-pick / verify / cross-reference without bouncing to a
// separate `mu task list --status CLOSED -w <ws>` shell.
//
// Per feat_card_8_recent (workstream `tui-impl`), promoted from the
// slot reserved by design_global_keymap. Mirrors the shape of
// Card 5 — Workspaces (264585f9), Card 6 — In-progress (760fc6c),
// and Card 7 — Blocked (4c50fc0) so the operator reads it the same
// way: TitledBox header, column-aligned rows, glanceable subtitle.
//
// CARD LAYOUT
//   glyph     id           STATUS   when      title
//   <ok>      feat_card_5  CLOSED   3m ago    FEAT: Card 5 — Workspaces
//   <ok>      feat_card_6  CLOSED   12m ago   FEAT: Card 6 — In-progress
//
// GLYPH.ok is coloured green — every row in this card is by definition
// CLOSED (the SDK helper listRecentClosed filters on status='CLOSED');
// the check-circle reads as "shipped".
//
// CLIPPING POLICY (per feat_column_aligned_lists)
//   id, STATUS, when → PROTECT (yankable / identifier / numeric)
//   title            → CLIP    (free-form prose)
//
// SUBTITLE
//   empty       → omitted (no recently-closed tasks → empty body)
//   populated   → "<N>"  or  "<N> · last <relTime since newest row>"
//                 — the newest updated_at is usually the latest close
//                 (a later note or edge on a CLOSED task also bumps it),
//                 the anchor for "did the wave just finish?".
//
// DATA
//   - snapshot.recentClosed is the listRecentClosed slice (CLOSED
//     status, ORDER BY updated_at DESC LIMIT 5). Used directly. No
//     SDK extension per the task brief — if a future task wants a
//     larger window, it should bump the SDK helper's default and
//     have the card cap at ROW_LIMIT here.
//
// "WHEN" COLUMN
//   The cheapest proxy for "closed at" is TaskRow.updatedAt: closeTask
//   sets it, but it is not close time. A real `closed_at` column would
//   need a schema add + agent_logs scan; OUT OF SCOPE per the brief.
//   If a row is later updated (a note appended, an edge added or
//   removed), touchTask bumps updatedAt, so the row moves up and its
//   "when" reads as the time of that later write. Known limitation.
//
// POPUP
//   Shift+8 (`*`) opens the matching Recent popup. Card slot 8 and
//   popup slot 8 now point at the same task-recent view again.

import type { ReactElement } from "react";
import { GLYPH as SHARED_GLYPH } from "../../../glyphs.js";
import type { WorkstreamSnapshot } from "../../../state.js";
import { formatPair } from "../../../tasks/status.js";
import { inkColorForPair } from "../../format.js";
import {
  type ColumnSpec,
  contentWidthFromCols,
  layoutColumns,
  renderRow,
  termColsForLayout,
} from "../columns.js";
import { ageMs, formatWhen } from "../format-helpers.js";
import { CARD_CONFIGS, cardRenderHeight } from "../layout.js";
import { ListRow } from "../list-row.js";
import { TitledBox } from "../titled-box.js";
import { CardPlaceholder } from "./_placeholder.js";

export interface RecentCardProps {
  snapshot: WorkstreamSnapshot | null;
  rowBudget?: number;
  cols?: number;
}

export const cardConfig = CARD_CONFIGS[8];

/** Glyph for every recently-closed row. Always the ok check. */
export const GLYPH = SHARED_GLYPH.ok;

const COLUMN_SPECS: ReadonlyArray<ColumnSpec> = [
  { kind: "protect" }, // glyph
  { kind: "protect" }, // task id
  { kind: "protect" }, // status (always CLOSED; constant for now)
  { kind: "protect", align: "right" }, // when (e.g. "3m ago")
  { kind: "clip", min: 1 }, // title
];

export function RecentCard({ snapshot, rowBudget, cols }: RecentCardProps): ReactElement {
  const contentWidth = contentWidthFromCols(cols ?? termColsForLayout());
  if (snapshot === null) {
    return CardPlaceholder({
      title: "Recent",
      cardId: 8,
      config: cardConfig,
      rowBudget,
      cols,
      text: "loading…",
    });
  }

  const { recentClosed } = snapshot;

  if (recentClosed.length === 0) {
    return CardPlaceholder({
      title: "Recent",
      cardId: 8,
      config: cardConfig,
      rowBudget,
      cols,
      text: "(none recently closed)",
    });
  }

  const now = Date.now();
  const ages = recentClosed.map((t) => ageMs(t, now));
  const subtitle = formatSubtitle(recentClosed.length, ages[0] ?? null);

  const shown = recentClosed.slice(0, rowBudget ?? cardConfig.maxRows);
  const more = recentClosed.length - shown.length;
  const bottomLabel = more > 0 ? `+${more} more · Shift+8` : undefined;
  const rows = shown.map((t, i) => [
    GLYPH,
    t.name,
    formatPair(t),
    formatWhen(ages[i] ?? null),
    t.title,
  ]);
  const widths = layoutColumns(rows, COLUMN_SPECS, contentWidth);

  return (
    <TitledBox
      height={cardRenderHeight(cardConfig, rowBudget)}
      width={cols}
      title="Recent"
      subtitle={subtitle}
      cardId={8}
      bottomLabel={bottomLabel}
    >
      {shown.map((t, i) => {
        const row = rows[i];
        if (row === undefined) return null;
        const padded = renderRow(row, widths, COLUMN_SPECS);
        const colors = [
          { color: "green" }, // glyph
          { bold: true }, // id
          { color: inkColorForPair(t) }, // status
          { dimColor: true }, // when
          { dimColor: true }, // title
        ];
        return <ListRow key={t.name} cells={padded} contentWidth={contentWidth} colors={colors} />;
      })}
    </TitledBox>
  );
}

// ─── pure helpers (exported for unit tests) ────────────────────────

/** Build the subtitle: total · last <when>. Suppresses the "last"
 *  leg when `mostRecentMs` is null (defensive — populated path
 *  always has at least one row, hence at least one age). */
export function formatSubtitle(total: number, mostRecentMs: number | null): string {
  const parts: string[] = [String(total)];
  if (mostRecentMs !== null) parts.push(`last ${formatWhen(mostRecentMs)}`);
  return parts.join(" · ");
}
